/* assets/gtender-opportunity-map-runtime.js — P869-C21 spatial presentation only.
 * Consumes the canonical GTender map response observed from gtender-public-entry.js.
 * It never owns Tender search, pagination, auth, persistence, or detail state.
 */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gtender-public-entry]';
  var MAP_RESPONSE_EVENT = 'gtender:c21-map-response';
  var DEFAULT_CENTER = [22.3193, 114.1694];
  var DEFAULT_ZOOM = 11;
  var MAX_AGGREGATES = 100;
  var MAX_TENDERS_PER_AGGREGATE = 20;
  var OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  var OSM_ATTRIBUTION = '© OpenStreetMap contributors';
  var PLACE_ID_RE = /^[A-Za-z0-9._:-]{1,220}$/;
  var TENDER_ID_RE = /^[A-Za-z0-9._:-]{3,240}$/;
  var roots = new WeakMap();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh(root) {
    return /^zh/i.test(
      clean(root && root.getAttribute('data-locale')) || clean(document.documentElement.lang)
    );
  }
  function reducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function responseData(payload) {
    if (!payload || typeof payload !== 'object') return {};
    var data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
    return data.map && typeof data.map === 'object' && !Array.isArray(data.map) ? data.map : data;
  }

  function canonicalPlaceFromAggregate(row) {
    if (!row || typeof row !== 'object') return null;
    var id = clean(row.place_id);
    var latitude = Number(row.latitude != null ? row.latitude : row.lat);
    var longitude = Number(row.longitude != null ? row.longitude : row.lng);
    if (!PLACE_ID_RE.test(id)) return null;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude < 20 || latitude > 24 || longitude < 112 || longitude > 116) return null;
    return {
      id: id,
      name: clean(row.place_name) || id,
      latitude: latitude,
      longitude: longitude,
    };
  }

  function aggregateEntries(payload) {
    var data = responseData(payload);
    var rawMappedCount = data.mapped_count;
    var mappedCount = Number(rawMappedCount);
    if (
      rawMappedCount !== null &&
      rawMappedCount !== undefined &&
      String(rawMappedCount).trim() !== '' &&
      Number.isSafeInteger(mappedCount) &&
      mappedCount === 0
    )
      return [];
    var rows = Array.isArray(data.aggregates)
      ? data.aggregates
      : Array.isArray(data.places)
        ? data.places
        : [];
    return rows
      .slice(0, MAX_AGGREGATES)
      .map(function (aggregate) {
        var place = canonicalPlaceFromAggregate(aggregate);
        return place ? { aggregate: aggregate, place: place } : null;
      })
      .filter(Boolean);
  }

  function aggregateTenderIds(aggregate) {
    return (
      Array.isArray(aggregate && aggregate.tender_ids)
        ? aggregate.tender_ids
        : Array.isArray(aggregate && aggregate.opportunity_ids)
          ? aggregate.opportunity_ids
          : []
    )
      .map(clean)
      .filter(function (id, index, all) {
        return TENDER_ID_RE.test(id) && all.indexOf(id) === index;
      })
      .slice(0, MAX_TENDERS_PER_AGGREGATE);
  }

  function listCardForTender(root, tenderId) {
    if (!TENDER_ID_RE.test(tenderId)) return null;
    return (
      Array.from(root.querySelectorAll('.gtender-partner-entry__card')).find((card) => {
        return Array.from(card.querySelectorAll('[data-gtender-detail-open]')).some((trigger) => {
          return clean(trigger.getAttribute('data-gtender-detail-open')) === tenderId;
        });
      }) || null
    );
  }

  function clearListSelection(root) {
    root.querySelectorAll('.gtender-partner-entry__card.is-selected').forEach((card) => {
      card.classList.remove('is-selected');
      const title = card.querySelector('button.gtender-card__title');
      if (title) title.removeAttribute('aria-current');
    });
  }

  function syncListSelection(root, aggregate, preferredTenderId, shouldScroll) {
    var ids = aggregateTenderIds(aggregate);
    var preferred = clean(preferredTenderId);
    if (TENDER_ID_RE.test(preferred) && ids.includes(preferred)) {
      ids = [preferred].concat(ids.filter((id) => id !== preferred));
    }
    var match = ids.map((id) => listCardForTender(root, id)).find(Boolean) || null;
    clearListSelection(root);
    if (!match) return;
    match.classList.add('is-selected');
    const title = match.querySelector('button.gtender-card__title');
    if (title) title.setAttribute('aria-current', 'true');
    if (shouldScroll && typeof match.scrollIntoView === 'function') {
      match.scrollIntoView({
        block: 'nearest',
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }
  }

  function ensureSelectedPanel(root) {
    var summary = root.querySelector('[data-gtender-map-summary]');
    if (!summary) return null;
    var panel = summary.querySelector('[data-gtender-map-selected]');
    if (panel) return panel;
    panel = document.createElement('section');
    panel.className = 'gtender-opportunity-map__selected';
    panel.setAttribute('data-gtender-map-selected', '');
    panel.setAttribute('aria-live', 'polite');
    panel.hidden = true;
    summary.appendChild(panel);
    return panel;
  }

  function tenderHref(id) {
    if (!TENDER_ID_RE.test(id)) return '';
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('tender', id);
      return url.toString();
    } catch (_error) {
      return '';
    }
  }

  function topBreakdown(aggregate) {
    var pairs = [];
    ['category_counts', 'service_theme_counts'].forEach(function (key) {
      var source = aggregate && aggregate[key];
      if (!source || typeof source !== 'object' || Array.isArray(source)) return;
      Object.keys(source).forEach(function (label) {
        var count = Number(source[label]);
        if (clean(label) && Number.isFinite(count) && count > 0) {
          pairs.push({ label: clean(label).replace(/[_-]+/g, ' '), count: count });
        }
      });
    });
    return pairs
      .sort(function (a, b) {
        return b.count - a.count || a.label.localeCompare(b.label);
      })
      .slice(0, 5);
  }

  function renderSelected(root, aggregate, place) {
    var panel = ensureSelectedPanel(root);
    if (!panel) return;
    var zh = isZh(root);
    panel.replaceChildren();
    panel.hidden = false;

    const header = document.createElement('header');
    header.className = 'gtender-place-card__header';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'gtender-place-card__eyebrow';
    eyebrow.textContent = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
      ? '招标地点'
      : zh
        ? '搜尋地區'
        : 'Search region';

    const heading = document.createElement('h3');
    heading.className = 'gtender-place-card__title';
    heading.textContent = place.name;

    const closeSelection = document.createElement('button');
    closeSelection.type = 'button';
    closeSelection.className = 'gtender-opportunity-map__selection-close';
    closeSelection.setAttribute('aria-label', zh ? '關閉地點卡片' : 'Close place card');
    closeSelection.textContent = '×';

    closeSelection.addEventListener('click', () => {
      const state = roots.get(root);
      if (state) clearSelection(root, state);

      const row = Array.from(root.querySelectorAll('[data-gtender-map-place]')).find(
        (candidate) => clean(candidate.getAttribute('data-gtender-map-place')) === place.id
      );

      if (row) row.focus();
    });

    header.append(eyebrow, heading, closeSelection);
    panel.appendChild(header);

    const rawCount = aggregate.opportunity_count;
    const parsedCount = Number(rawCount);
    const countKnown =
      rawCount !== null &&
      rawCount !== undefined &&
      String(rawCount).trim() !== '' &&
      Number.isSafeInteger(parsedCount) &&
      parsedCount >= 0;
    const count = countKnown ? parsedCount : 0;

    const metrics = document.createElement('dl');
    metrics.className = 'gtender-place-card__metrics';

    const metricRows = [
      [zh ? '相關公告' : 'Related notices', countKnown ? String(count) : '—'],
      [zh ? '即將截止' : 'Closing soon', '—'],
      [zh ? '相關項目' : 'Projects', '—'],
      [zh ? '合作夥伴' : 'Suppliers', '—'],
    ];

    metricRows.forEach(([label, value]) => {
      const item = document.createElement('div');
      const term = document.createElement('dt');
      const number = document.createElement('dd');
      term.textContent = label;
      number.textContent = value;
      item.append(term, number);
      metrics.appendChild(item);
    });

    panel.appendChild(metrics);

    const property = document.createElement('section');
    property.className = 'gtender-place-card__property';

    const propertyTitle = document.createElement('h4');
    propertyTitle.textContent = zh ? '物業資料' : 'Property';

    const propertyNote = document.createElement('p');
    propertyNote.textContent = zh
      ? '屋苑、建築與規劃資料：待確認。'
      : 'Estate, building and planning information: not yet verified.';

    property.append(propertyTitle, propertyNote);
    panel.appendChild(property);

    const evidence = document.createElement('p');
    evidence.className = 'gtender-place-card__evidence';
    evidence.textContent = zh
      ? '— 表示尚未取得資料，不代表數量為零。'
      : '— means data is unavailable, not a zero count.';
    panel.appendChild(evidence);

    var breakdown = topBreakdown(aggregate);
    if (breakdown.length) {
      var chips = document.createElement('div');
      chips.className = 'gtender-opportunity-map__breakdown';
      breakdown.forEach(function (entry) {
        var chip = document.createElement('span');
        chip.textContent = entry.label + ' · ' + entry.count;
        chips.appendChild(chip);
      });
      panel.appendChild(chips);
    }

    var ids = aggregateTenderIds(aggregate);

    var title = document.createElement('h4');
    title.textContent = zh ? '相關公告' : 'Related Notices';
    panel.appendChild(title);

    if (!ids.length) {
      var unavailable = document.createElement('p');
      unavailable.textContent = zh
        ? '暫未取得可開啟的機會詳情。'
        : 'Opportunity details are not available yet.';
      panel.appendChild(unavailable);
      return;
    }

    var list = document.createElement('div');
    list.className = 'gtender-opportunity-map__tenders';
    ids.forEach(function (id) {
      var href = tenderHref(id);
      if (!href) return;
      var link = document.createElement('a');
      link.className = 'gtender-opportunity-map__tender-link';
      link.href = href;
      link.setAttribute('data-gtender-map-tender', id);
      link.setAttribute('data-gtender-detail-open', id);
      link.setAttribute('aria-haspopup', 'dialog');

      const detailDialog = root.querySelector('[data-gtender-public-detail]');

      if (detailDialog && detailDialog.id) {
        link.setAttribute('aria-controls', detailDialog.id);
      }

      link.textContent = zh ? '查看公告：' + id : 'View notice: ' + id;

      list.appendChild(link);
    });
    panel.appendChild(list);

    if (countKnown && count > ids.length) {
      const bounded = document.createElement('p');
      bounded.className = 'gtender-opportunity-map__bounded-note';
      bounded.textContent = zh
        ? '此處列出 ' + ids.length + ' 個可查看的機會；相關機會共 ' + count + ' 個。'
        : 'Listing ' +
          ids.length +
          ' viewable opportunities out of ' +
          count +
          ' related opportunities.';
      panel.appendChild(bounded);
    }
  }

  const VERSION = 'gtender-c21-dual-engine-01';
  const FIRST_LOAD_TIMEOUT_MS = 12000;
  const activeRoots = new Set();

  function stateFor(root) {
    let state = roots.get(root);
    if (state) return state;
    state = {
      engine: {
        preferred: 'mapbox',
        active: null,
        state: 'idle',
        generation: 0,
        fallbackLocked: false,
        firstLoadPassed: false,
      },
      adapter: null,
      places: [],
      selectedPlace: null,
      camera: null,
      lastAcceptedPayload: null,
      observer: null,
      destroyed: false,
      dataState: 'idle',
      rawRowCount: 0,
      listSelectionListener: null,
    };
    roots.set(root, state);
    activeRoots.add(root);
    root.setAttribute('data-gtender-map-runtime-state', 'idle');
    return state;
  }

  function words(root, zh, en) {
    return isZh(root) ? zh : en;
  }

  function engineState(root, state, value) {
    state.engine.state = value;
    root.setAttribute('data-gtender-map-engine-state', value);
    root.setAttribute('data-gtender-map-engine', state.engine.active || 'none');
  }

  function showEngineNotice(root, message) {
    const canvas = root.querySelector('[data-gtender-map-canvas]');
    if (!canvas) return;
    let notice = canvas.querySelector('[data-gtender-map-engine-notice]');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'gtender-opportunity-map__engine-notice';
      notice.setAttribute('data-gtender-map-engine-notice', '');
      notice.setAttribute('role', 'status');
      canvas.appendChild(notice);
    }
    notice.textContent = message || '';
    notice.hidden = !message;
  }

  function resetSurface(root, kind) {
    const canvas = root.querySelector('[data-gtender-map-canvas]');
    if (!canvas) throw new Error('map canvas missing');
    const node = document.createElement('div');
    node.className = 'gtender-opportunity-map__surface gtender-opportunity-map__' + kind;
    node.setAttribute('data-gtender-map-surface', kind);
    node.setAttribute('aria-label', words(root, '招標地圖', 'Tender map'));
    node.setAttribute('role', 'region');
    canvas.replaceChildren(node);
    return node;
  }

  function clearSelection(root, state) {
    state.selectedPlace = null;
    clearListSelection(root);
    const panel = root.querySelector('[data-gtender-map-selected]');
    if (panel) {
      panel.hidden = true;
      panel.replaceChildren();
    }
  }

  function current(root, state, generation) {
    return (
      !state.destroyed &&
      root.isConnected &&
      roots.get(root) === state &&
      state.engine.generation === generation
    );
  }

  function disposeAdapter(state) {
    const previous = state.adapter;
    state.adapter = null;
    if (previous) {
      try {
        previous.destroy();
      } catch (_) {}
    }
  }

  function fatalMapboxError(error) {
    if (!error) return false;
    const message = clean(error.message).toLowerCase();
    const url = clean(error.url || (error.request && error.request.url)).toLowerCase();
    const status = Number(error.status || error.statusCode);
    // A failed tile, glyph, sprite or optional source is not a bootstrap failure.
    const resourceRequest =
      /\/(?:fonts|glyphs|tiles|v4)\//.test(url) ||
      /\/styles\/v1\/[^/]+\/[^/?#]+\/(?:sprite|tiles)/.test(url) ||
      /\b(?:tile|glyph|sprite)\b/.test(message);
    if (resourceRequest) return false;
    const styleRequest = /\/styles\/v1\/[^/]+\/[^/?#]+(?:[?#]|$)/.test(url);
    return (
      (styleRequest && [401, 403, 404].includes(status)) ||
      /invalid access token|access token is required|invalid style|style.*not found|failed to load style/.test(
        message
      )
    );
  }

  function markerButton(root, entry) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gtender-opportunity-map__marker';
    const raw = entry.aggregate.opportunity_count;
    const number = Number(raw);
    const known = raw != null && clean(raw) !== '' && Number.isSafeInteger(number) && number >= 0;
    button.textContent = known ? String(number) : '—';
    button.setAttribute('aria-label', words(root, '查看 ', 'View ') + entry.place.name);
    button.title = entry.place.name;
    return button;
  }
  function createMapboxAdapter(root, callbacks) {
    if (!window.mapboxgl || typeof window.mapboxgl.Map !== 'function') {
      throw new Error('mapbox dependency unavailable');
    }
    const token = clean(root.getAttribute('data-mapbox-public-token'));
    const style = clean(root.getAttribute('data-mapbox-style-url'));
    if (!token.startsWith('pk.') || !/^mapbox:\/\/styles\/[^/]+\/[^/?#]+$/.test(style)) {
      throw new Error('mapbox configuration unavailable');
    }
    const node = resetSurface(root, 'mapbox');
    let map = null;
    let dead = false;
    let loaded = false;
    let timer = null;
    let entriesById = new Map();
    const markers = new Map();
    const sourceId = 'gtender-c21-places';

    function destroy() {
      if (dead) return;
      dead = true;
      window.clearTimeout(timer);
      markers.forEach((marker) => marker.remove());
      markers.clear();
      if (map) map.remove();
    }
    function camera() {
      const center = map.getCenter();
      return { latitude: center.lat, longitude: center.lng, zoom: map.getZoom() };
    }
    function removeMarkers() {
      markers.forEach((marker) => marker.remove());
      markers.clear();
    }
    function syncVisibleMarkers() {
      if (dead || !loaded || !map.getSource(sourceId) || !map.isSourceLoaded(sourceId)) return;
      const visible = new Set();
      map.querySourceFeatures(sourceId).forEach((feature) => {
        const properties = feature.properties || {};
        const clustered = Boolean(properties.cluster);
        const key = clustered ? 'cluster:' + properties.cluster_id : 'place:' + properties.place_id;
        if (visible.has(key)) return;
        visible.add(key);
        if (markers.has(key)) return;
        const entry = entriesById.get(String(properties.place_id));
        if (!clustered && !entry) return;
        const button = clustered ? document.createElement('button') : markerButton(root, entry);
        if (clustered) {
          button.type = 'button';
          button.className = 'gtender-opportunity-map__marker';
          button.textContent = String(properties.point_count);
          button.setAttribute(
            'aria-label',
            words(root, '放大查看地點群組：', 'Zoom to place cluster: ') + properties.point_count
          );
          button.addEventListener('click', () => {
            const source = map.getSource(sourceId);
            if (dead || !source) return;
            source.getClusterExpansionZoom(Number(properties.cluster_id), (error, zoom) => {
              if (dead || error) return;
              map.easeTo({
                center: feature.geometry.coordinates,
                zoom,
                duration: reducedMotion() ? 0 : 400,
              });
            });
          });
        } else {
          button.addEventListener('click', () => {
            const latest = entriesById.get(entry.place.id);
            if (!dead && latest) callbacks.select(latest);
          });
        }
        const marker = new window.mapboxgl.Marker({ element: button, anchor: 'center' })
          .setLngLat(feature.geometry.coordinates)
          .addTo(map);
        markers.set(key, marker);
      });
      markers.forEach((marker, key) => {
        if (!visible.has(key)) {
          marker.remove();
          markers.delete(key);
        }
      });
    }
    function collection(entries) {
      return {
        type: 'FeatureCollection',
        features: entries.map((entry) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [entry.place.longitude, entry.place.latitude] },
          properties: { place_id: entry.place.id },
        })),
      };
    }
    function renderPlaces(entries) {
      entriesById = new Map(entries.map((entry) => [entry.place.id, entry]));
      removeMarkers();
      const source = map.getSource(sourceId);
      if (source) source.setData(collection(entries));
    }
    const adapter = {
      renderPlaces,
      clearPlaces() {
        renderPlaces([]);
      },
      focusPlace(place) {
        map.flyTo({
          center: [place.longitude, place.latitude],
          zoom: Math.max(map.getZoom(), 14),
          duration: reducedMotion() ? 0 : 500,
        });
      },
      fitPlaces(entries) {
        if (!entries.length) return;
        if (entries.length === 1) {
          adapter.focusPlace(entries[0].place);
          return;
        }
        const bounds = new window.mapboxgl.LngLatBounds();
        entries.forEach(({ place }) => bounds.extend([place.longitude, place.latitude]));
        map.fitBounds(bounds, { padding: 36, maxZoom: 15, duration: reducedMotion() ? 0 : 500 });
      },
      resize() {
        if (!dead) map.resize();
      },
      camera,
      restoreCamera(value) {
        map.jumpTo({ center: [value.longitude, value.latitude], zoom: value.zoom });
      },
      destroy,
    };
    try {
      map = new window.mapboxgl.Map({
        container: node,
        accessToken: token,
        style,
        center: [114.1694, 22.3193],
        zoom: DEFAULT_ZOOM,
        attributionControl: true,
        scrollZoom: false,
        dragRotate: false,
        touchPitch: false,
      });
      if (map.touchZoomRotate) map.touchZoomRotate.disableRotation();
      if (map.keyboard && map.keyboard.disableRotation) map.keyboard.disableRotation();
      map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', () => {
        if (dead || loaded) return;
        loaded = true;
        window.clearTimeout(timer);
        try {
          map.addSource(sourceId, {
            type: 'geojson',
            data: collection([]),
            cluster: true,
            clusterRadius: 52,
            clusterMaxZoom: 14,
          });
          // Invisible style layer keeps the source participating in map rendering.
          map.addLayer({
            id: sourceId + '-anchor',
            source: sourceId,
            type: 'circle',
            paint: { 'circle-radius': 1, 'circle-opacity': 0 },
          });
          callbacks.ready(adapter);
        } catch (_) {
          // First successful Mapbox load locks the engine; a product-layer error is not fallback.
          callbacks.ready(adapter);
          callbacks.warning();
        }
      });
      map.on('render', syncVisibleMarkers);
      map.on('moveend', () => {
        if (!dead && loaded) callbacks.camera(camera());
      });
      // Preserve deliberate camera movement even before the first basemap load.
      map.on('dragend', () => {
        if (!dead) callbacks.camera(camera());
      });
      map.on('zoomend', (event) => {
        if (!dead && !loaded && event && event.originalEvent) callbacks.camera(camera());
      });
      map.on('error', (event) => {
        if (dead) return;
        if (!loaded && fatalMapboxError(event && event.error))
          callbacks.fail('mapbox-fatal-before-load');
        else if (loaded) callbacks.warning();
      });
      timer = window.setTimeout(() => {
        if (!dead && !loaded) callbacks.fail('mapbox-first-load-timeout');
      }, FIRST_LOAD_TIMEOUT_MS);
      return adapter;
    } catch (error) {
      destroy();
      throw error;
    }
  }

  function createLeafletAdapter(root, callbacks) {
    if (!window.L || typeof window.L.map !== 'function')
      throw new Error('leaflet dependency unavailable');
    const node = resetSurface(root, 'leaflet');
    let map = null;
    let layer = null;
    let timer = null;
    let dead = false;
    let loaded = false;
    function destroy() {
      if (dead) return;
      dead = true;
      window.clearTimeout(timer);
      if (layer) layer.clearLayers();
      if (map) map.remove();
    }
    const adapter = {
      renderPlaces(entries) {
        layer.clearLayers();
        entries.forEach((entry) => {
          const button = markerButton(root, entry);
          // DOM nodes preserve safe text rendering and native keyboard activation.
          const icon = window.L.divIcon({
            className: 'gtender-opportunity-map__marker-shell',
            html: button,
            iconSize: [44, 44],
            iconAnchor: [22, 22],
          });
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            if (!dead) callbacks.select(entry);
          });
          const marker = window.L.marker([entry.place.latitude, entry.place.longitude], {
            icon,
            keyboard: false,
            title: entry.place.name,
          });
          const tooltip = document.createElement('span');
          tooltip.textContent = entry.place.name;
          marker.bindTooltip(tooltip, { direction: 'top' });
          layer.addLayer(marker);
        });
      },
      clearPlaces() {
        if (layer) layer.clearLayers();
      },
      focusPlace(place) {
        map.flyTo([place.latitude, place.longitude], Math.max(map.getZoom(), 14), {
          animate: !reducedMotion(),
        });
      },
      fitPlaces(entries) {
        if (!entries.length) return;
        if (entries.length === 1) {
          adapter.focusPlace(entries[0].place);
          return;
        }
        map.fitBounds(
          entries.map(({ place }) => [place.latitude, place.longitude]),
          { padding: [36, 36], maxZoom: 15, animate: !reducedMotion() }
        );
      },
      resize() {
        if (!dead) map.invalidateSize(false);
      },
      camera() {
        const center = map.getCenter();
        return { latitude: center.lat, longitude: center.lng, zoom: map.getZoom() };
      },
      restoreCamera(value) {
        map.setView([value.latitude, value.longitude], value.zoom, { animate: false });
      },
      destroy,
    };
    try {
      map = window.L.map(node, {
        zoomControl: true,
        scrollWheelZoom: false,
        zoomAnimation: !reducedMotion(),
        fadeAnimation: !reducedMotion(),
        markerZoomAnimation: !reducedMotion(),
      }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      layer =
        typeof window.L.markerClusterGroup === 'function'
          ? window.L.markerClusterGroup({
              showCoverageOnHover: false,
              maxClusterRadius: 52,
              animate: !reducedMotion(),
            })
          : window.L.layerGroup();
      layer.addTo(map);
      const tiles = window.L.tileLayer(OSM_URL, { maxZoom: 19, attribution: OSM_ATTRIBUTION });
      tiles.once('tileload', () => {
        if (dead || loaded) return;
        loaded = true;
        window.clearTimeout(timer);
        callbacks.ready(adapter);
      });
      map.on('moveend', () => {
        if (!dead && loaded) callbacks.camera(adapter.camera());
      });
      map.on('dragend', () => {
        if (!dead) callbacks.camera(adapter.camera());
      });
      timer = window.setTimeout(() => {
        if (!dead && !loaded) callbacks.fail('leaflet-first-tile-timeout');
      }, FIRST_LOAD_TIMEOUT_MS);
      tiles.addTo(map);
      return adapter;
    } catch (error) {
      destroy();
      throw error;
    }
  }

  function syncDataCopy() {}

  function renderCurrent(root, state, fit) {
    if (!state.adapter || !['mapbox-ready', 'leaflet-ready'].includes(state.engine.state)) return;
    state.adapter.renderPlaces(state.places);
    if (state.selectedPlace) {
      const selectedTenderId = clean(state.selectedPlace.tenderId);
      const entry = state.places.find((item) => item.place.id === state.selectedPlace.place.id);
      if (entry) {
        const retainedTenderId = aggregateTenderIds(entry.aggregate).includes(selectedTenderId)
          ? selectedTenderId
          : '';
        state.selectedPlace = {
          aggregate: entry.aggregate,
          place: entry.place,
          tenderId: retainedTenderId,
        };
        renderSelected(root, entry.aggregate, entry.place);
        syncListSelection(root, entry.aggregate, retainedTenderId, false);
        state.adapter.focusPlace(entry.place);
        return;
      }
      clearSelection(root, state);
    }
    if (state.camera) state.adapter.restoreCamera(state.camera);
    else if (fit) state.adapter.fitPlaces(state.places);
  }

  function launchEngine(root, state, kind) {
    const generation = ++state.engine.generation;
    disposeAdapter(state);
    state.engine.active = kind;
    state.engine.firstLoadPassed = false;
    engineState(root, state, kind === 'mapbox' ? 'mapbox-loading' : 'fallback-switching');
    const callbacks = {
      ready(adapter) {
        if (!current(root, state, generation)) {
          adapter.destroy();
          return;
        }
        state.adapter = adapter;
        state.engine.firstLoadPassed = true;
        engineState(root, state, kind === 'mapbox' ? 'mapbox-ready' : 'leaflet-ready');
        showEngineNotice(
          root,
          kind === 'leaflet'
            ? words(
                root,
                '目前使用後備底圖。招標資料與篩選條件維持不變。',
                'Using the fallback basemap. Tender data and filters are unchanged.'
              )
            : ''
        );
        try {
          renderCurrent(root, state, true);
          adapter.resize();
        } catch (_) {
          callbacks.warning();
        }
      },
      fail(reason) {
        if (!current(root, state, generation) || state.engine.firstLoadPassed) return;
        if (kind === 'mapbox' && !state.engine.fallbackLocked) {
          state.engine.fallbackLocked = true;
          root.setAttribute('data-gtender-map-fallback-reason', reason);
          launchEngine(root, state, 'leaflet');
        } else {
          ++state.engine.generation;
          disposeAdapter(state);
          state.engine.active = null;
          engineState(root, state, 'unavailable');
          showEngineNotice(
            root,
            words(
              root,
              '地圖暫時無法使用，請切回列表查看招標公告。',
              'The map is unavailable. Switch to List to view tender notices.'
            )
          );
        }
      },
      select(entry) {
        if (current(root, state, generation)) selectPlace(root, entry.aggregate, entry.place);
      },
      camera(value) {
        if (current(root, state, generation)) state.camera = value;
      },
      warning() {
        if (current(root, state, generation))
          showEngineNotice(
            root,
            words(
              root,
              '部分地圖資源暫時無法載入；目前不會切換地圖引擎。',
              'Some map resources are unavailable. The active map engine is unchanged.'
            )
          );
      },
    };
    try {
      const adapter =
        kind === 'mapbox'
          ? createMapboxAdapter(root, callbacks)
          : createLeafletAdapter(root, callbacks);
      if (current(root, state, generation)) state.adapter = adapter;
      else adapter.destroy();
    } catch (_) {
      callbacks.fail(kind + '-initialization-unavailable');
    }
  }

  function ensureEngine(root, state) {
    if (
      state.destroyed ||
      !root.isConnected ||
      root.getAttribute('data-gtender-view-mode') !== 'map'
    )
      return;
    if (!state.adapter && state.engine.state === 'idle') launchEngine(root, state, 'mapbox');
    const generation = state.engine.generation;
    window.requestAnimationFrame(() => {
      if (current(root, state, generation) && state.adapter) state.adapter.resize();
    });
  }

  function selectPlace(root, aggregate, place, preferredTenderId, shouldScrollList) {
    const state = stateFor(root);
    const preferred = clean(preferredTenderId);
    state.selectedPlace = {
      aggregate,
      place,
      tenderId: TENDER_ID_RE.test(preferred) ? preferred : '',
    };
    renderSelected(root, aggregate, place);
    syncListSelection(root, aggregate, preferred, shouldScrollList !== false);
    if (state.adapter && state.engine.firstLoadPassed) state.adapter.focusPlace(place);
  }

  function syncRows(root, entries) {
    root.querySelectorAll('[data-gtender-map-place]').forEach((row) => {
      const id = clean(row.getAttribute('data-gtender-map-place'));
      const entry = entries.find((item) => item.place.id === id);
      if (!entry) return;
      const name = row.querySelector('.gtender-opportunity-map__place-name');
      if (name) name.textContent = entry.place.name;
      row.tabIndex = 0;
      row.setAttribute('role', 'button');
      row.setAttribute('data-gtender-place-resolved', 'true');
      row.setAttribute('aria-label', words(root, '查看 ', 'View ') + entry.place.name);
      if (row.hasAttribute('data-gtender-map-place-bound')) return;
      row.setAttribute('data-gtender-map-place-bound', 'true');
      row.addEventListener('click', () => {
        const latest = stateFor(root).places.find((item) => item.place.id === id);
        if (latest) selectPlace(root, latest.aggregate, latest.place);
      });
      row.addEventListener('keydown', (event) => {
        if (!['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        row.click();
      });
    });
  }

  function renderPayload(root, payload) {
    const state = stateFor(root);
    state.lastAcceptedPayload = payload;
    state.places = aggregateEntries(payload);
    const data = responseData(payload);
    const rows = Array.isArray(data.aggregates)
      ? data.aggregates
      : Array.isArray(data.places)
        ? data.places
        : [];
    state.rawRowCount = rows.length;
    state.dataState = state.places.length ? 'mapped' : rows.length ? 'no-resolved-places' : 'empty';
    root.setAttribute('data-gtender-map-runtime-state', state.dataState);
    root.setAttribute('data-gtender-map-enrichment-limit', String(MAX_AGGREGATES));
    root.setAttribute('data-gtender-map-resolved-count', String(state.places.length));
    root.setAttribute(
      'data-gtender-map-unresolved-place-count',
      String(Math.max(0, rows.length - state.places.length))
    );
    if (
      state.selectedPlace &&
      !state.places.some((entry) => entry.place.id === state.selectedPlace.place.id)
    ) {
      clearSelection(root, state);
    }
    // A newly accepted search result gets its own fit intent, unless selection survives.
    if (!state.selectedPlace) state.camera = null;
    syncRows(root, state.places);
    syncDataCopy(root, state);
    ensureEngine(root, state);
    try {
      renderCurrent(root, state, true);
    } catch (_) {}
  }

  function mount(root) {
    const state = stateFor(root);
    if (!state.listSelectionListener) {
      state.listSelectionListener = (event) => {
        if (root.getAttribute('data-gtender-view-mode') !== 'map') return;
        const target = event.target;
        const trigger =
          target && target.closest ? target.closest('[data-gtender-detail-open]') : null;
        if (!trigger || !root.contains(trigger)) return;
        const tenderId = clean(trigger.getAttribute('data-gtender-detail-open'));
        if (!TENDER_ID_RE.test(tenderId)) return;
        const entry = state.places.find((item) => {
          return aggregateTenderIds(item.aggregate).includes(tenderId);
        });
        if (entry) {
          selectPlace(root, entry.aggregate, entry.place, tenderId, false);
        }
      };
      root.addEventListener('click', state.listSelectionListener);
    }
    if (!state.observer) {
      ensureSelectedPanel(root);
      state.observer = new MutationObserver((records) => {
        if (
          records.some((record) => record.attributeName === 'data-gtender-map-state') &&
          root.getAttribute('data-gtender-map-state') === 'unavailable'
        ) {
          state.dataState = 'error';
          state.places = [];
          state.lastAcceptedPayload = null;
          clearSelection(root, state);
          root.setAttribute('data-gtender-map-runtime-state', 'error');
          if (state.adapter && state.engine.firstLoadPassed) state.adapter.clearPlaces();
          syncDataCopy(root, state);
        }
        ensureEngine(root, state);
      });
      state.observer.observe(root, {
        attributes: true,
        attributeFilter: ['data-gtender-view-mode', 'data-gtender-map-state'],
      });
    }
    ensureEngine(root, state);
  }

  function eachRoot(scope, callback) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) callback(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(callback);
  }
  function boot(scope) {
    eachRoot(scope || document, mount);
  }
  function destroy(root) {
    const state = roots.get(root);
    if (!state) return;
    state.destroyed = true;
    ++state.engine.generation;
    if (state.observer) state.observer.disconnect();
    if (state.listSelectionListener) {
      root.removeEventListener('click', state.listSelectionListener);
      state.listSelectionListener = null;
    }
    disposeAdapter(state);
    roots.delete(root);
    activeRoots.delete(root);
  }

  if (
    window.__GTENDER_OPPORTUNITY_MAP_RUNTIME__ &&
    window.__GTENDER_OPPORTUNITY_MAP_RUNTIME__.version === VERSION
  ) {
    window.__GTENDER_OPPORTUNITY_MAP_RUNTIME__.boot(document);
    return;
  }
  document.addEventListener(MAP_RESPONSE_EVENT, (event) => {
    const root = event.target;
    const detail = event.detail;
    if (
      !root ||
      !root.matches ||
      !root.matches(ROOT_SELECTOR) ||
      !root.isConnected ||
      !detail ||
      !detail.payload ||
      root.getAttribute('data-gtender-view-mode') !== 'map'
    )
      return;
    mount(root);
    renderPayload(root, detail.payload);
  });
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => eachRoot(event.target, destroy));
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) Array.from(activeRoots).forEach(destroy);
  });
  window.addEventListener('pageshow', () => boot(document));
  window.__GTENDER_OPPORTUNITY_MAP_RUNTIME__ = Object.freeze({
    version: VERSION,
    boot,
    destroy,
    maxAggregates: MAX_AGGREGATES,
    firstLoadTimeoutMs: FIRST_LOAD_TIMEOUT_MS,
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else boot(document);
})();
